import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, getAuthHeaders } from "./AuthContext";
import { useUnreadMessages } from "./UnreadMessagesContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JobNotificationsContext = createContext({ unreadCount: 0, refetchUnread: () => {}, subscribeToJobNotifications: () => () => {}, markApplicationsRead: () => {} });

// Avisos laborales (nuevo postulante, entrevista, oferta) — SEPARADO del
// contador de Mensajes (UnreadMessagesContext). Reutiliza el mismo socket
// WS ya abierto por Mensajes (subscribeToMessages) en vez de abrir uno
// nuevo; solo escucha el tipo de evento "job_notification". El backend
// (`db.notifications`) ya excluye por completo los mensajes de chat, así
// que este contador nunca se mezcla con el de Mensajes.
export function JobNotificationsProvider({ children }) {
  const { user } = useAuth();
  const { subscribeToMessages } = useUnreadMessages();
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastNotification, setLastNotification] = useState(null);

  const refetchUnread = useCallback(async () => {
    if (!user) { setUnreadCount(0); return; }
    try {
      const res = await axios.get(`${API}/my/notifications/unread-count`, { headers: getAuthHeaders() });
      setUnreadCount(res.data?.unread_count || 0);
    } catch { /* keep last known value on a transient failure */ }
  }, [user]);

  useEffect(() => {
    refetchUnread();
    if (!user) return;
    const interval = setInterval(refetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user, refetchUnread]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((data) => {
      if (data.type !== "job_notification") return;
      setUnreadCount((c) => c + 1);
      setLastNotification(data.notification);
    });
    return unsubscribe;
  }, [subscribeToMessages]);

  // Called when the user actually opens/visualizes the application(s) —
  // never just for opening the app.
  const markApplicationsRead = useCallback(async (applicationIds) => {
    const ids = (applicationIds || []).filter(Boolean);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(`${API}/my/notifications/mark-read`, { application_ids: ids }, { headers: getAuthHeaders() });
      const marked = res.data?.marked_read || 0;
      if (marked) setUnreadCount((c) => Math.max(0, c - marked));
    } catch { /* non-critical — badge self-heals on next poll */ }
  }, []);

  const subscribeToJobNotifications = useCallback((callback) => {
    return subscribeToMessages((data) => {
      if (data.type === "job_notification") callback(data.notification);
    });
  }, [subscribeToMessages]);

  return (
    <JobNotificationsContext.Provider value={{ unreadCount, refetchUnread, subscribeToJobNotifications, markApplicationsRead, lastNotification }}>
      {children}
    </JobNotificationsContext.Provider>
  );
}

export function useJobNotifications() {
  return useContext(JobNotificationsContext);
}
